import { formatDistanceToNowStrict, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers';
import {
  OrganizationIntegrationStatus,
  OrganizationIntegrationStatusData,
} from '@/modules/common/types/integration-status';
import { useSynchronizeIntegration } from '@/modules/settings/hooks';

import {
  ConflictsState,
  ErrorState,
  OutdatedState,
  SyncingState,
  UpToDateState,
} from './refresh-card-states';

interface AllIntegrationsRefreshCardProps {
  integrationStatusData: OrganizationIntegrationStatusData;
}

export const AllIntegrationsRefreshCard = ({
  integrationStatusData,
}: AllIntegrationsRefreshCardProps) => {
  const { t } = useTranslation(['authenticated.settings']);

  const synchronizeMutation = useSynchronizeIntegration({
    currentStatus: integrationStatusData.status,
    onSuccess: () => {
      displayToastSuccess(t('authenticated.settings:pages.integrations.refreshCard.successToast'));
    },
    onError: () => {
      displayToastError(t('authenticated.settings:pages.integrations.refreshCard.errorToast'));
    },
  });

  const { status } = integrationStatusData;

  const handleRefreshAll = () => {
    synchronizeMutation.mutate(undefined);
  };

  // Get min lastSyncedAt to determine outdated state
  const getMinLastSyncedAt = (): Date | undefined => {
    const syncedDates = integrationStatusData.integrations
      .map(i => i.lastSyncedAt)
      .filter((date): date is Date => date !== undefined);

    if (syncedDates.length === 0) return undefined;

    return syncedDates.reduce((min, current) => (current < min ? current : min));
  };

  const getTimeAgoText = (): string => {
    const minLastSyncedAt = getMinLastSyncedAt();
    if (!minLastSyncedAt) return '';

    const timeAgo = formatDistanceToNowStrict(minLastSyncedAt, { addSuffix: true });
    return t('authenticated.settings:pages.integrations.refreshCard.refreshedAgo', {
      time: timeAgo,
    });
  };

  const isOutdated = (): boolean => {
    const minLastSyncedAt = getMinLastSyncedAt();
    if (!minLastSyncedAt) return false;

    const oneDayAgo = subDays(new Date(), 1);
    return minLastSyncedAt < oneDayAgo;
  };

  const renderContent = () => {
    const timeAgoText = getTimeAgoText();

    // Render Unknown as Syncing
    if (status === OrganizationIntegrationStatus.Unknown) {
      return <SyncingState />;
    }

    switch (status) {
      case OrganizationIntegrationStatus.Syncing:
        return <SyncingState />;

      case OrganizationIntegrationStatus.Idle:
        // Show outdated state if last sync was more than 1 day ago
        if (isOutdated()) {
          return <OutdatedState timeAgoText={timeAgoText} onRefresh={handleRefreshAll} />;
        }

        return <UpToDateState timeAgoText={timeAgoText} onRefresh={handleRefreshAll} />;

      case OrganizationIntegrationStatus.SyncFailed:
        return <ErrorState onRefresh={handleRefreshAll} />;

      case OrganizationIntegrationStatus.HasConflicts:
        return <ConflictsState />;

      default:
        return null;
    }
  };

  return (
    <div className="mb-6 flex flex-col gap-1 rounded-lg border bg-sidebar p-4">
      {renderContent()}
    </div>
  );
};
